import api from '@/services/api';
import type {
    DealerPeriodStatement,
    FundAccountPair,
    PairPeriodStatement,
    PeriodStatementResult,
    PeriodStatementRow,
    StatementPeriod,
    UnassignedMovement,
} from '../types/dealerFundStatementPeriod.types';
import { periodQueryParams } from '../utils/statementPeriod';
import { sameAmount, sameUnsplit, sameVatRates } from '../utils/vatFigures';
import {
    assertWindow,
    readBoundedNullable,
    readBoundedText,
    readMatchingId,
    readUuid,
    STATEMENT_LINE_KEY,
} from './periodPayloadChecks';
import { hasApiCode, NOT_ENTITLED_CODE, payloadReaders, UNVERIFIED_CODE } from './statementPayloadReaders';
import { vatPayloadReaders } from './vatPayloadReaders';

const MAX_ROWS = 100;
const PAIRS_PER_PAGE = 500;
const MAX_PAIR_PAGES = 20;
const CURRENCY = 'GBP';
const SCALE = 2;

const { malformed, readRecord, readText, readMoney, readInt, readIsoDate, readArray } =
    payloadReaders('fund statement period payload');

const { readBlock, readLineVat, readStatementVat } = vatPayloadReaders('fund statement period payload');

const readBounded = (value: unknown, label: string, max: number): unknown[] => {
    const list = readArray(value, label);
    if (list.length > max) throw malformed(`${label} has more than ${max} entries`);
    return list;
};

const mapRow = (value: unknown): PeriodStatementRow => {
    const raw = readRecord(value, 'row');
    const trancode = readText(raw.trancode, 'trancode');
    if (!STATEMENT_LINE_KEY.test(trancode)) throw malformed('trancode is not a valid code');
    const amount = readMoney(raw.amount, 'amount');
    const postings = readInt(raw.postings, 'postings', 0);
    return {
        trancode,
        name: readBoundedNullable(raw.name, 'name', 255),
        amount,
        postings,
        block: readBlock(raw.block),
        ...readLineVat(raw, amount, postings, 'row'),
    };
};

const PAIR_VAT_KEYS = ['vat', 'net', 'vat_rates', 'unsplit'] as const;

const mapPair = (value: unknown): FundAccountPair => {
    const raw = readRecord(value, 'pair');
    const amount = readMoney(raw.amount, 'amount');
    const postings = readInt(raw.postings, 'postings', 0);
    const carriesVat = PAIR_VAT_KEYS.some((key) => raw[key] !== undefined);
    return {
        sellingDealerId: readUuid(raw.selling_dealer_id, 'selling_dealer_id'),
        dealerCode: readBoundedNullable(raw.dealer_code, 'dealer_code', 255),
        productId: readUuid(raw.product_id, 'product_id'),
        setCode: readBoundedNullable(raw.set_code, 'set_code', 36),
        sageAccountCode: readBoundedNullable(raw.sage_account_code, 'sage_account_code', 20),
        sageAccountName: readBoundedNullable(raw.sage_account_name, 'sage_account_name', 100),
        accountCount: readInt(raw.account_count, 'account_count', 0),
        amount,
        postings,
        vatFigures: carriesVat ? readLineVat(raw, amount, postings, 'pair') : null,
    };
};

const mapUnassigned = (value: unknown): UnassignedMovement => {
    const raw = readRecord(value, 'unassigned');
    return {
        accountCount: readInt(raw.account_count, 'unassigned account_count', 0),
        amount: readMoney(raw.amount, 'unassigned amount'),
        postings: readInt(raw.postings, 'unassigned postings', 0),
    };
};

const readBase = (raw: Record<string, unknown>, layer: string, period: StatementPeriod) => {
    if (raw.layer !== layer) throw malformed(`layer is not ${layer}`);
    if (raw.scale !== SCALE) throw malformed('scale is not 2');
    if (raw.currency !== CURRENCY) throw malformed('currency is not GBP');
    if (raw.check !== 'verified') throw malformed('check is not verified');
    const from = readIsoDate(raw.from, 'from');
    const to = readIsoDate(raw.to, 'to');
    assertWindow(period, from, to);
    readBoundedNullable(raw.set_code, 'set_code', 36);
    const rows = readBounded(raw.rows, 'rows', MAX_ROWS).map(mapRow);
    return {
        currency: CURRENCY,
        roundingMode: readBoundedText(raw.rounding_mode, 'rounding_mode', 32),
        from,
        to,
        openingBalance: readMoney(raw.opening_balance, 'opening_balance'),
        closingBalance: readMoney(raw.closing_balance, 'closing_balance'),
        ...readStatementVat(raw, rows),
        postings: readInt(raw.postings, 'postings', 0),
        rows,
    };
};

interface DealerPairPage {
    statement: DealerPeriodStatement;
    total: number;
    lastPage: number;
}

const readPairsPage = (value: unknown, page: number): { total: number; lastPage: number } => {
    const raw = readRecord(value, 'pairs_page');
    const total = readInt(raw.total, 'pairs_page total', 0);
    const lastPage = readInt(raw.last_page, 'pairs_page last_page', 1);
    if (raw.page !== page || raw.per_page !== PAIRS_PER_PAGE) throw malformed('pairs_page does not match the request');
    if (lastPage !== Math.max(1, Math.ceil(total / PAIRS_PER_PAGE))) throw malformed('pairs_page last_page is inconsistent');
    return { total, lastPage };
};

const mapDealerPage = (data: unknown, dealerId: string, period: StatementPeriod, page: number): DealerPairPage => {
    const raw = readRecord(data, 'root');
    const id = readMatchingId(raw.dealer_id, 'dealer_id', dealerId);
    const { total, lastPage } = readPairsPage(raw.pairs_page, page);
    const pairs = readBounded(raw.pairs, 'pairs', PAIRS_PER_PAGE).map(mapPair);
    if (pairs.length !== Math.min(PAIRS_PER_PAGE, Math.max(0, total - (page - 1) * PAIRS_PER_PAGE))) {
        throw malformed('pairs does not match pairs_page');
    }
    const statement = {
        dealerId: id,
        ...readBase(raw, 'dealer', period),
        pairs,
        unassigned: mapUnassigned(raw.unassigned),
    };
    return { statement, total, lastPage };
};

const sameRow = (a: PeriodStatementRow, b: PeriodStatementRow): boolean =>
    a.trancode === b.trancode &&
    a.amount === b.amount &&
    a.postings === b.postings &&
    a.block === b.block &&
    sameAmount(a.vat, b.vat) &&
    sameAmount(a.net, b.net) &&
    sameVatRates(a.vatRates, b.vatRates) &&
    sameUnsplit(a.unsplit, b.unsplit);

const sameStatement = (a: DealerPeriodStatement, b: DealerPeriodStatement): boolean =>
    a.openingBalance === b.openingBalance &&
    a.closingBalance === b.closingBalance &&
    sameAmount(a.openingBalanceNet, b.openingBalanceNet) &&
    sameAmount(a.closingBalanceNet, b.closingBalanceNet) &&
    sameUnsplit(a.unsplit, b.unsplit) &&
    sameUnsplit(a.balanceUnsplit, b.balanceUnsplit) &&
    a.postings === b.postings &&
    a.rows.length === b.rows.length &&
    a.rows.every((row, index) => sameRow(row, b.rows[index])) &&
    a.unassigned.amount === b.unassigned.amount &&
    a.unassigned.postings === b.unassigned.postings;

const mapPairStatement = (
    data: unknown,
    sellingDealerId: string,
    productId: string,
    period: StatementPeriod,
): PairPeriodStatement => {
    const raw = readRecord(data, 'root');
    const dealer = readMatchingId(raw.dealer_id, 'dealer_id', sellingDealerId);
    const product = readMatchingId(raw.product_id, 'product_id', productId);
    return {
        sellingDealerId: dealer,
        productId: product,
        dealerCode: readBoundedNullable(raw.dealer_code, 'dealer_code', 255),
        sageAccountCode: readBoundedNullable(raw.sage_account_code, 'sage_account_code', 20),
        sageAccountName: readBoundedNullable(raw.sage_account_name, 'sage_account_name', 100),
        ...readBase(raw, 'pair', period),
    };
};

const readResult = async <T>(
    path: string,
    period: StatementPeriod,
    map: (data: unknown) => T,
    extraParams: Record<string, number> = {},
): Promise<PeriodStatementResult<T>> => {
    try {
        const response = await api.get(path, {
            params: { ...periodQueryParams(period), ...extraParams },
            suppressGlobalForbiddenToast: true,
        });
        return { status: 'ok', statement: map(response.data?.data) };
    } catch (error) {
        if (hasApiCode(error, 503, UNVERIFIED_CODE)) return { status: 'unverified' };
        if (hasApiCode(error, 403, NOT_ENTITLED_CODE)) return { status: 'not_entitled' };
        throw error;
    }
};

const fetchDealerPairPage = (
    dealerId: string,
    period: StatementPeriod,
    page: number,
): Promise<PeriodStatementResult<DealerPairPage>> =>
    readResult(
        `/dealers/${dealerId}/fund-statement/periods`,
        period,
        (data) => mapDealerPage(data, dealerId, period, page),
        { page, per_page: PAIRS_PER_PAGE },
    );

const assertUniquePairs = (pairs: FundAccountPair[]): void => {
    const keys = new Set(pairs.map((pair) => `${pair.sellingDealerId}|${pair.productId}`.toLowerCase()));
    if (keys.size !== pairs.length) throw malformed('pairs repeats a fund account');
};

const assertUniformPairVat = (pairs: FundAccountPair[]): void => {
    const withFigures = pairs.filter((pair) => pair.vatFigures !== null).length;
    if (withFigures !== 0 && withFigures !== pairs.length) throw malformed('pairs carry VAT figures on some pairs only');
};

export const fetchDealerPeriodStatement = async (
    dealerId: string,
    period: StatementPeriod,
): Promise<PeriodStatementResult<DealerPeriodStatement>> => {
    const first = await fetchDealerPairPage(dealerId, period, 1);
    if (first.status !== 'ok') return first;
    const { statement, lastPage, total } = first.statement;
    if (lastPage > MAX_PAIR_PAGES) throw malformed(`pairs span more than ${MAX_PAIR_PAGES} pages`);
    const pairs = [...statement.pairs];
    for (let page = 2; page <= lastPage; page += 1) {
        const next = await fetchDealerPairPage(dealerId, period, page);
        if (next.status !== 'ok') return next;
        if (next.statement.total !== total) throw malformed('pairs total changed while paging pairs');
        if (!sameStatement(statement, next.statement.statement)) throw malformed('statement changed while paging pairs');
        pairs.push(...next.statement.statement.pairs);
    }
    if (pairs.length !== total) throw malformed('pairs does not match pairs_page total');
    assertUniquePairs(pairs);
    assertUniformPairVat(pairs);
    return { status: 'ok', statement: { ...statement, pairs } };
};

export const fetchPairPeriodStatement = (
    sellingDealerId: string,
    productId: string,
    period: StatementPeriod,
): Promise<PeriodStatementResult<PairPeriodStatement>> =>
    readResult(`/dealers/${sellingDealerId}/fund-statement/periods/${productId}`, period, (data) =>
        mapPairStatement(data, sellingDealerId, productId, period),
    );
